import styled from '@emotion/styled';
import { ComponentProps, ReactNode, use, useCallback, useMemo } from 'react';
import { SongPreview, VideoProvider } from '~/interfaces';
import Box from '~/modules/Elements/AKUI/Primitives/Box';
import { BackgroundContext } from '~/modules/Elements/BackgroundContext';
import { typography } from '~/modules/Elements/cssMixins';
import { useSongStats } from '~/modules/Songs/stats/hooks';
import { FeatureFlags } from '~/modules/utils/featureFlags';
import useFeatureFlag from '~/modules/utils/useFeatureFlag';
import { GraphicSetting, MobilePhoneModeSetting, useSettingValue } from '~/routes/Settings/SettingsState';
import SongFlag from '~/routes/SingASong/SongSelection/Components/SongCard/SongFlag';
import { TopContainer } from '~/routes/SingASong/SongSelection/Components/SongCard/TopContainer';

interface Props extends ComponentProps<typeof SongCardContainer> {
  song: SongPreview;
  focused: boolean;
  songId?: string;
  groupLetter?: string;
  expanded?: boolean;
  background?: boolean;
  handleClick?: (songId: string, groupLetter?: string) => void;
  video?: ReactNode;
  isPopular: boolean;
  forceFlag: boolean;
  locked?: boolean;
  'data-expanded'?: boolean;
}

function inferProvider(song: Pick<SongPreview, 'provider' | 'uid' | 'url' | 'id'>): VideoProvider {
  if (song.provider) return song.provider;

  if (song.uid?.startsWith('dailymotion:')) return 'dailymotion';
  if (song.uid?.startsWith('vimeo:')) return 'vimeo';
  if (song.uid?.startsWith('youtube:')) return 'youtube';

  if (song.url?.includes('dailymotion.com')) return 'dailymotion';
  if (song.url?.includes('vimeo.com')) return 'vimeo';
  if (song.url?.includes('youtube.com') || song.url?.includes('youtu.be')) return 'youtube';

  if (song.id?.startsWith('dailymotion-')) return 'dailymotion';
  if (song.id?.startsWith('vimeo-')) return 'vimeo';

  return 'youtube';
}

function getSongBackgroundImage(song: SongPreview): string | undefined {
  const provider = inferProvider(song);

  switch (provider) {
    case 'youtube':
      return `https://i3.ytimg.com/vi/${song.video}/hqdefault.jpg`;

    case 'dailymotion':
      return `https://www.dailymotion.com/thumbnail/video/${song.video}`;

    case 'vimeo':
      return undefined;

    default:
      return undefined;
  }
}

export const FinalSongCard = ({
  song,
  focused,
  video,
  children,
  songId,
  groupLetter,
  handleClick,
  isPopular,
  background = true,
  forceFlag = false,
  locked = false,
  ...restProps
}: Props) => {
  const { theme } = use(BackgroundContext);
  const expanded = restProps['data-expanded'] ?? false;

  const onClickCallback = useCallback(
    () => (handleClick ? handleClick(songId!, groupLetter) : undefined),
    [handleClick, songId, groupLetter],
  );

  const [graphicSetting] = useSettingValue(GraphicSetting);

  const backgroundImage = useMemo(() => getSongBackgroundImage(song), [song]);

  return (
    <SongCardContainer
      {...restProps}
      onClick={handleClick ? onClickCallback : undefined}
      data-locked={locked}
      aria-disabled={locked || undefined}>
      {background && (
        <SongCardBackground
          style={backgroundImage ? { backgroundImage: `url('${backgroundImage}')` } : undefined}
          data-has-image={Boolean(backgroundImage)}
          data-focused={expanded ? false : focused}
          data-expanded={restProps['data-expanded']}
          data-graphic-setting={graphicSetting}
          data-theme={theme}
          data-locked={locked}
        />
      )}

      <SongInfo data-expanded={expanded}>
        {!expanded && <TopContainer song={song} isPopular={isPopular} video={video} />}
        <SongListEntryDetailsArtist data-expanded={expanded}>{song.artist}</SongListEntryDetailsArtist>
        <SongListEntryDetailsTitle data-expanded={expanded}>{song.title}</SongListEntryDetailsTitle>

        <ExpandedData data-expanded={expanded}>
          {expanded && (
            <>
              {song.author && (
                <SongAuthor expanded={expanded}>
                  by&nbsp;
                  {song.authorUrl ? (
                    <a href={song.authorUrl} target="_blank" rel="noreferrer">
                      {song.author}
                    </a>
                  ) : (
                    song.author
                  )}
                </SongAuthor>
              )}
              <SongListEntryStats song={song} />
            </>
          )}
          {!expanded && <Language song={song} forceFlag={forceFlag} />}
        </ExpandedData>
      </SongInfo>

      {locked && !expanded && (
        <LockedSongOverlay>
          <strong>Register for free</strong>
          <span>Unlock full catalog</span>
        </LockedSongOverlay>
      )}

      {children}
      {video}
    </SongCardContainer>
  );
};

const LockedSongOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 3;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 16px;
  box-sizing: border-box;
  color: #fff;
  text-align: center;
  background: linear-gradient(180deg, rgba(2, 8, 18, 0.28), rgba(2, 8, 18, 0.72));
  text-shadow: 0 2px 12px rgba(0, 0, 0, 0.92);
  pointer-events: none;

  strong {
    font-size: clamp(16px, 1.8vw, 24px);
    line-height: 1;
    font-weight: 900;
    text-transform: uppercase;
  }

  span {
    color: rgba(255, 255, 255, 0.82);
    font-size: clamp(11px, 1vw, 14px);
    font-weight: 800;
  }

  @media (max-width: 760px) {
    gap: 3px;
    padding: 10px;

    strong {
      font-size: clamp(15px, 4.6vw, 20px);
      line-height: 1.05;
    }

    span {
      font-size: clamp(10px, 3.1vw, 13px);
    }
  }
`;

export const Language = styled(SongFlag)`
  height: 2.75rem;
  object-fit: cover;
  border-top-right-radius: 0.4rem;
  border-bottom-left-radius: 0.4rem;
  position: absolute;
  z-index: -1;
  left: 0rem;
  bottom: 0rem;
  opacity: 0.95;
`;

export const ExpandedData = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
`;

const SongInfo = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  z-index: 1;
  box-sizing: border-box;
  flex-direction: column;

  &[data-expanded='true'] {
    align-items: flex-start;
    justify-content: flex-start;
  }

  @media (max-width: 760px) {
    &:not([data-expanded='true']) {
      align-items: stretch;
      justify-content: center;
      padding: 0.85rem;
      border-radius: 0.8rem;
      background: linear-gradient(180deg, rgba(4, 8, 16, 0.34), rgba(4, 8, 16, 0.86));
      backdrop-filter: blur(2.4px);
    }
  }
`;

export const SongCardContainer = styled.div`
  font-size: 4.5rem;

  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  flex-direction: column;
  box-sizing: border-box;
  position: relative;
  padding: 0.5rem;

  border: 0.2rem solid rgba(255, 255, 255, 0.72);
  border-radius: 1rem;
  overflow: hidden;
  box-shadow: 0 1.2rem 3rem rgba(0, 0, 0, 0.2);
`;

export const SongCardBackground = styled.div`
  background-color: #2b2b2b;
  border-radius: 0.8rem;
  box-shadow: inset 0 0 0 0.25rem transparent;

  &[data-expanded='true'] {
    border-radius: 0;
  }

  position: absolute;
  z-index: -1;
  inset: 0;
  background-position: center center;
  background-repeat: no-repeat;
  background-size: cover;

  &[data-has-image='false'] {
    background-image: none;
    background-color: #2b2b2b;
  }

  &[data-locked='true'] {
    filter: grayscale(100%) brightness(0.42) blur(3px) !important;
    opacity: 0.86 !important;
  }

  &[data-graphic-setting='high'] {
    transition: 300ms;

    &[data-focused='true'] {
      background-size: 100%;
      opacity: 1;
      box-shadow: inset 0 0 0 0.45rem var(--walky-orange);
    }

    &[data-focused='false'] {
      background-size: 110%;
      filter: grayscale(90%);
      opacity: 0.8;
    }

    @media (max-width: 760px) {
      filter: saturate(0.82) brightness(0.56) blur(2px);
      opacity: 0.92;

      &[data-focused='true'],
      &[data-focused='false'] {
        background-size: cover;
        filter: saturate(0.82) brightness(0.56) blur(2px);
        opacity: 0.92;
      }
    }

    &[data-expanded='true'] {
      filter: blur(10px);
    }

    &[data-theme='eurovision'] {
      opacity: 0.95;
    }
  }

  &[data-graphic-setting='low'] {
    background-size: 100%;
    opacity: 0.6;

    &[data-focused='true'] {
      opacity: 1;
    }
  }
`;

export const SongListEntryDetails = styled(Box)<{ expanded?: boolean }>`
  background: rgba(31, 31, 33, 0.88);

  width: auto;
  display: inline-block;
  padding: 0.55rem 0.75rem;
  ${typography};

  text-align: right;
  font-size: ${(props) => {
    if (props.expanded) return '6rem';
    const fewerSongsPerRowEnabled = useFeatureFlag(FeatureFlags.FewerSongsPerRow);
    const [mobilePhoneMode] = useSettingValue(MobilePhoneModeSetting);
    const shouldUseLargerFont = !mobilePhoneMode && fewerSongsPerRowEnabled;
    return shouldUseLargerFont ? '3.5rem' : '2.7rem';
  }};

  &[data-expanded='true'] {
    font-size: 6rem;
  }

  @media (max-width: 760px) {
    &:not([data-expanded='true']) {
      width: 100%;
      box-sizing: border-box;
      display: block;
      padding: 0.2rem 0.5rem;
      text-align: center;
      background: transparent;
      text-shadow:
        0 0.15rem 0.35rem rgba(0, 0, 0, 0.95),
        0 0 1.2rem rgba(0, 0, 0, 0.75);
    }
  }
`;

export const SongListEntryDetailsArtist = styled(SongListEntryDetails)`
  color: var(--walky-orange);

  @media (max-width: 760px) {
    &:not([data-expanded='true']) {
      font-size: 2.65rem;
      line-height: 1.05;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
`;

export const SongListEntryDetailsTitle = styled(SongListEntryDetails)`
  margin-top: ${(props) => (props.expanded ? '1.5rem' : '0.5rem')};
  color: white;
  font-size: ${(props) => {
    if (props.expanded) return '6rem';
    const fewerSongsPerRowEnabled = useFeatureFlag(FeatureFlags.FewerSongsPerRow);
    const [mobilePhoneMode] = useSettingValue(MobilePhoneModeSetting);
    const shouldUseLargerFont = !mobilePhoneMode && fewerSongsPerRowEnabled;
    return shouldUseLargerFont ? '4rem' : '2.7rem';
  }};

  @media (max-width: 760px) {
    &:not([data-expanded='true']) {
      margin-top: 0.25rem;
      font-size: 3.65rem;
      line-height: 1.02;
      overflow: hidden;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 3;
    }
  }
`;

export const SongAuthor = styled(SongListEntryDetailsTitle)`
  font-size: 3rem;
  margin-top: 3rem;
`;

export const SongListEntryStats = ({ song }: { song: SongPreview }) => {
  const stats = useSongStats(song);

  return (
    <SongAuthor>
      {stats?.plays ? `Played ${stats.plays} time${stats.plays > 1 ? 's' : ''}` : 'Never played yet'}
    </SongAuthor>
  );
};
